import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronDown, ChevronUp, Users, User, AlertTriangle } from 'lucide-react';
import { PListOrderDto } from '../../../../../services/plistOrderService';
import { UserProfileDto } from '../../../../../types/userProfile';
import { ShipmentLineEditor } from '../../../shared/ShipmentLineEditor';
import { ShipmentLineRow, ShipmentLineDto } from '../../../../../types/shipmentLine';
import { shipmentLineService } from '../../../../../services/shipmentLineService';

interface DeliveryInfoData {
  totalVolume: string;
  totalWeight: string;
  numOfPackages: string;
  numOfPallets: string;
  carrierName: string;
  packZoneID: string;
  packageRemark: string;
}

interface PListOrderDeliveryData extends DeliveryInfoData {
  orderNo: number;
  expanded: boolean;
  shipmentLinesExpanded: boolean;
  shipmentLines: ShipmentLineRow[];
  // Address info for display
  customerName?: string | null;
  delAddress1?: string | null;
  delCity?: string | null;
}

type DeliveryMode = 'single' | 'consolidate' | 'per-order' | 'forced-per-order';
type AggregationMethod = 'copy' | 'divide';

interface DeliveryInfoModalProps {
  isOpen: boolean;
  pListId: number;
  companyNo: string;
  pListOrders: PListOrderDto[];
  userProfile: UserProfileDto | null;
  onConfirm: (
    pListData: DeliveryInfoData,
    pListOrdersData: Map<number, DeliveryInfoData>,
    shipmentLinesData?: Map<number, ShipmentLineRow[]>,
    consolidate?: boolean
  ) => Promise<void>;
  onClose: () => void;
}

// Helper to create an address key for comparison
const getAddressKey = (order: PListOrderDto): string => {
  return [
    order.customerNo || '',
    order.delName || '',
    order.delAddress1 || '',
    order.delPostNo || '',
    order.delCity || ''
  ].join('|').toLowerCase().trim();
};

// Check if all orders have the same delivery address
const allOrdersSameAddress = (orders: PListOrderDto[]): boolean => {
  if (orders.length <= 1) return true;
  const firstKey = getAddressKey(orders[0]);
  return orders.every(o => getAddressKey(o) === firstKey);
};

export const DeliveryInfoModal: React.FC<DeliveryInfoModalProps> = ({
  isOpen,
  pListId,
  companyNo,
  pListOrders,
  userProfile,
  onConfirm,
  onClose
}) => {
  // Determine scenario
  const scenario = useMemo(() => {
    if (pListOrders.length === 0) return 'no-orders';
    if (pListOrders.length === 1) return 'single';
    return allOrdersSameAddress(pListOrders) ? 'multi-same-address' : 'multi-different-address';
  }, [pListOrders]);

  // Delivery mode state
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('single');
  const [aggregationMethod, setAggregationMethod] = useState<AggregationMethod | null>(null);
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [showAggregationSelection, setShowAggregationSelection] = useState(false);

  // Initialize delivery mode based on scenario
  useEffect(() => {
    if (scenario === 'single' || scenario === 'no-orders') {
      setDeliveryMode('single');
      setShowModeSelection(false);
    } else if (scenario === 'multi-different-address') {
      setDeliveryMode('forced-per-order');
      setShowModeSelection(false);
    } else if (scenario === 'multi-same-address') {
      // Show mode selection for same address
      setShowModeSelection(true);
      setDeliveryMode('consolidate'); // Default to consolidate
    }
  }, [scenario]);

  const [pListData, setPListData] = useState<DeliveryInfoData>({
    totalVolume: '',
    totalWeight: '',
    numOfPackages: '',
    numOfPallets: '',
    carrierName: '',
    packZoneID: '',
    packageRemark: ''
  });

  const [pListOrdersData, setPListOrdersData] = useState<PListOrderDeliveryData[]>(
    pListOrders.map(order => ({
      orderNo: order.orderNo,
      totalVolume: order.totalVolume?.toString() || '',
      totalWeight: order.totalWeight?.toString() || '',
      numOfPackages: order.numOfPackages?.toString() || '',
      numOfPallets: order.numOfPallets?.toString() || '',
      carrierName: order.carrierName || '',
      packZoneID: order.packZoneID || '',
      packageRemark: order.packageRemark || '',
      expanded: false,
      shipmentLinesExpanded: false,
      shipmentLines: [],
      customerName: order.customerName,
      delAddress1: order.delAddress1,
      delCity: order.delCity
    }))
  );

  const [shipmentLinesForConsolidation, setShipmentLinesForConsolidation] = useState<ShipmentLineRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingShipmentLines, setIsLoadingShipmentLines] = useState(false);

  // Sync pListOrdersData when pListOrders prop changes
  useEffect(() => {
    setPListOrdersData(
      pListOrders.map(order => ({
        orderNo: order.orderNo,
        totalVolume: order.totalVolume?.toString() || '',
        totalWeight: order.totalWeight?.toString() || '',
        numOfPackages: order.numOfPackages?.toString() || '',
        numOfPallets: order.numOfPallets?.toString() || '',
        carrierName: order.carrierName || '',
        packZoneID: order.packZoneID || '',
        packageRemark: order.packageRemark || '',
        expanded: false,
        shipmentLinesExpanded: false,
        shipmentLines: [],
        customerName: order.customerName,
        delAddress1: order.delAddress1,
        delCity: order.delCity
      }))
    );
  }, [pListOrders]);

  // Load existing shipment lines when modal opens
  useEffect(() => {
    const loadShipmentLines = async () => {
      if (!isOpen || pListOrders.length === 0) return;

      setIsLoadingShipmentLines(true);
      try {
        const shipmentLinesPromises = pListOrders.map(order =>
          shipmentLineService.getByPListOrder(pListId, order.orderNo)
            .then(lines => ({ orderNo: order.orderNo, lines }))
            .catch(() => ({ orderNo: order.orderNo, lines: [] as ShipmentLineDto[] }))
        );

        const results = await Promise.all(shipmentLinesPromises);

        setPListOrdersData(prev => prev.map(order => {
          const result = results.find(r => r.orderNo === order.orderNo);
          if (result && result.lines.length > 0) {
            const mappedLines: ShipmentLineRow[] = result.lines.map(line => ({
              lineID: line.lineID,
              transitPackagingID: line.transitPackagingID,
              transitPackagingDescr: line.transitPackagingDescr,
              description1: line.description1,
              numOfPackages: line.numOfPackages,
              volume: line.volume,
              weight: line.weight,
              height: line.height,
              width: line.width,
              length: line.length,
              pListID: line.pListID,
              orderNo: line.orderNo,
              sorting: line.sorting,
              isNew: false
            }));
            return { ...order, shipmentLines: mappedLines };
          }
          return order;
        }));
      } catch (error) {
        console.error('Failed to load shipment lines:', error);
      } finally {
        setIsLoadingShipmentLines(false);
      }
    };

    loadShipmentLines();
  }, [isOpen, pListId, pListOrders]);

  if (!isOpen) return null;

  const handlePListDataChange = (field: keyof DeliveryInfoData, value: string) => {
    setPListData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePListOrderDataChange = (orderNo: number, field: keyof DeliveryInfoData, value: string) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, [field]: value }
          : order
      )
    );
  };

  const toggleOrderExpanded = (orderNo: number) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, expanded: !order.expanded }
          : order
      )
    );
  };

  const handleShipmentLinesChange = (orderNo: number, lines: ShipmentLineRow[]) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, shipmentLines: lines }
          : order
      )
    );
  };

  const handleShipmentLinesExpandedChange = (orderNo: number, expanded: boolean) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, shipmentLinesExpanded: expanded }
          : order
      )
    );
  };

  const handleModeSelect = (mode: 'consolidate' | 'per-order') => {
    setDeliveryMode(mode);
    if (mode === 'consolidate') {
      setShowAggregationSelection(true);
    } else {
      setShowAggregationSelection(false);
      setAggregationMethod(null);
    }
  };

  const handleAggregationSelect = (method: AggregationMethod) => {
    setAggregationMethod(method);
    setShowAggregationSelection(false);
  };

  const validateRequiredFields = (): string | null => {
    const errors: string[] = [];

    // Check PList required fields
    if (userProfile?.pListWeightRequired && !pListData.totalWeight) {
      errors.push('Vekt er påkrevd');
    }
    if (userProfile?.pListVolumeRequired && !pListData.totalVolume) {
      errors.push('Volum er påkrevd');
    }
    if (userProfile?.pListNumOfPackagesRequired && !pListData.numOfPackages) {
      errors.push('Antall kolli er påkrevd');
    }
    if (userProfile?.pListNumOfPalletsRequired && !pListData.numOfPallets) {
      errors.push('Antall paller er påkrevd');
    }

    if (errors.length > 0) {
      return 'Følgende felter er påkrevd:\n' + errors.join('\n');
    }

    return null;
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      const validationError = validateRequiredFields();
      if (validationError) {
        alert(validationError);
        return;
      }

      setIsSubmitting(true);

      // Convert pListOrdersData to Map
      const ordersMap = new Map<number, DeliveryInfoData>();
      const shipmentLinesMap = new Map<number, ShipmentLineRow[]>();

      // Handle different delivery modes
      if (deliveryMode === 'single' || scenario === 'single') {
        // 1:1 scenario - auto-copy PList data to the single order
        const singleOrder = pListOrdersData[0];
        if (singleOrder) {
          ordersMap.set(singleOrder.orderNo, {
            totalVolume: pListData.totalVolume,
            totalWeight: pListData.totalWeight,
            numOfPackages: pListData.numOfPackages,
            numOfPallets: pListData.numOfPallets,
            carrierName: pListData.carrierName,
            packZoneID: pListData.packZoneID,
            packageRemark: pListData.packageRemark
          });

          // Use per-order shipment lines if any
          if (singleOrder.shipmentLines && singleOrder.shipmentLines.length > 0) {
            shipmentLinesMap.set(singleOrder.orderNo, singleOrder.shipmentLines);
          }
        }
      } else if (deliveryMode === 'consolidate' && aggregationMethod) {
        // Consolidation mode - apply PList data to all orders
        pListOrdersData.forEach(order => {
          if (aggregationMethod === 'copy') {
            // Copy identical values to all orders
            ordersMap.set(order.orderNo, {
              totalVolume: pListData.totalVolume,
              totalWeight: pListData.totalWeight,
              numOfPackages: pListData.numOfPackages,
              numOfPallets: pListData.numOfPallets,
              carrierName: pListData.carrierName,
              packZoneID: pListData.packZoneID,
              packageRemark: pListData.packageRemark
            });
          } else {
            // Divide values equally among orders
            const numOrders = pListOrdersData.length;
            const dividedVolume = pListData.totalVolume ? (parseFloat(pListData.totalVolume) / numOrders).toFixed(4) : '';
            const dividedWeight = pListData.totalWeight ? (parseFloat(pListData.totalWeight) / numOrders).toFixed(2) : '';
            const dividedPackages = pListData.numOfPackages ? Math.ceil(parseInt(pListData.numOfPackages) / numOrders).toString() : '';
            const dividedPallets = pListData.numOfPallets ? (parseFloat(pListData.numOfPallets) / numOrders).toFixed(2) : '';

            ordersMap.set(order.orderNo, {
              totalVolume: dividedVolume,
              totalWeight: dividedWeight,
              numOfPackages: dividedPackages,
              numOfPallets: dividedPallets,
              carrierName: pListData.carrierName,
              packZoneID: pListData.packZoneID,
              packageRemark: pListData.packageRemark
            });
          }
        });

        // For consolidation, shipment lines go to the first order (will be linked to Shipment)
        if (shipmentLinesForConsolidation.length > 0) {
          shipmentLinesMap.set(pListOrdersData[0].orderNo, shipmentLinesForConsolidation);
        }
      } else {
        // Per-order mode - use individual order data
        pListOrdersData.forEach(order => {
          ordersMap.set(order.orderNo, {
            totalVolume: order.totalVolume,
            totalWeight: order.totalWeight,
            numOfPackages: order.numOfPackages,
            numOfPallets: order.numOfPallets,
            carrierName: order.carrierName,
            packZoneID: order.packZoneID,
            packageRemark: order.packageRemark
          });

          // Include shipment lines if any exist
          if (order.shipmentLines && order.shipmentLines.length > 0) {
            shipmentLinesMap.set(order.orderNo, order.shipmentLines);
          }
        });
      }

      const shouldConsolidate = deliveryMode === 'consolidate' && aggregationMethod !== null;
      await onConfirm(
        pListData,
        ordersMap,
        shipmentLinesMap.size > 0 ? shipmentLinesMap : undefined,
        shouldConsolidate
      );
    } catch (error) {
      console.error('Error submitting delivery info:', error);
      alert('Kunne ikke lagre leveranseinformasjon. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDeliveryInfoFields = (
    data: DeliveryInfoData,
    onChange: (field: keyof DeliveryInfoData, value: string) => void,
    readOnly: boolean = false
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* 1. Antall kolli */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Antall kolli{userProfile?.pListNumOfPackagesRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListNumOfPackagesRequired && !data.numOfPackages ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.numOfPackages}
          onChange={(e) => onChange('numOfPackages', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListNumOfPackagesRequired}
        />
      </div>

      {/* 2. Antall paller */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Antall paller{userProfile?.pListNumOfPalletsRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.01"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListNumOfPalletsRequired && !data.numOfPallets ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.numOfPallets}
          onChange={(e) => onChange('numOfPallets', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListNumOfPalletsRequired}
        />
      </div>

      {/* 3. Vekt */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Vekt (kg){userProfile?.pListWeightRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.01"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListWeightRequired && !data.totalWeight ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.totalWeight}
          onChange={(e) => onChange('totalWeight', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListWeightRequired}
        />
      </div>

      {/* 4. Volum */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Volum (m³){userProfile?.pListVolumeRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.0001"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListVolumeRequired && !data.totalVolume ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.totalVolume}
          onChange={(e) => onChange('totalVolume', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListVolumeRequired}
        />
      </div>

      {/* 5. Transportør */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Transportør
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={data.carrierName}
          onChange={(e) => onChange('carrierName', e.target.value)}
          readOnly={readOnly}
        />
      </div>

      {/* 6. Pakkesone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pakkesone
        </label>
        <input
          type="text"
          list={readOnly ? undefined : "packzone-options"}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Skann eller velg pakkesone"
          value={data.packZoneID}
          onChange={(e) => onChange('packZoneID', e.target.value)}
          readOnly={readOnly}
        />
        {!readOnly && (
          <datalist id="packzone-options">
            <option value="ZONE-A" />
            <option value="ZONE-B" />
            <option value="ZONE-C" />
            <option value="ZONE-D" />
            <option value="PAKKING-01" />
            <option value="PAKKING-02" />
          </datalist>
        )}
      </div>

      {/* 7. Pakkemerknad */}
      <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pakkemerknad
        </label>
        <textarea
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Skriv inn eventuelle merknader"
          value={data.packageRemark}
          onChange={(e) => onChange('packageRemark', e.target.value)}
          readOnly={readOnly}
        />
      </div>
    </div>
  );

  // Render mode selection for multi-order same address
  const renderModeSelection = () => (
    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h4 className="text-lg font-semibold text-blue-900 mb-2">
        {pListOrders.length} ordrer med samme leveringsadresse
      </h4>
      <p className="text-sm text-blue-700 mb-4">
        Alle ordrene har samme leveringsadresse. Hvordan vil du registrere leveranseinformasjon?
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => handleModeSelect('consolidate')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
            deliveryMode === 'consolidate'
              ? 'border-blue-600 bg-blue-100 text-blue-900'
              : 'border-gray-300 bg-white hover:border-blue-400 text-gray-700'
          }`}
        >
          <Users size={20} />
          <span className="font-medium">Samlasting</span>
        </button>
        <button
          onClick={() => handleModeSelect('per-order')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
            deliveryMode === 'per-order'
              ? 'border-blue-600 bg-blue-100 text-blue-900'
              : 'border-gray-300 bg-white hover:border-blue-400 text-gray-700'
          }`}
        >
          <User size={20} />
          <span className="font-medium">Per ordre</span>
        </button>
      </div>
    </div>
  );

  // Render aggregation method selection
  const renderAggregationSelection = () => (
    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <h4 className="text-lg font-semibold text-yellow-900 mb-2">
        Hvordan skal verdiene fordeles?
      </h4>
      <p className="text-sm text-yellow-700 mb-4">
        Velg hvordan antall kolli, vekt og volum skal fordeles på ordrene.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => handleAggregationSelect('copy')}
          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
            aggregationMethod === 'copy'
              ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
              : 'border-gray-300 bg-white hover:border-yellow-400 text-gray-700'
          }`}
        >
          <div className="font-medium mb-1">Kopier identisk</div>
          <div className="text-xs text-gray-500">Samme verdier på alle ordrer</div>
        </button>
        <button
          onClick={() => handleAggregationSelect('divide')}
          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
            aggregationMethod === 'divide'
              ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
              : 'border-gray-300 bg-white hover:border-yellow-400 text-gray-700'
          }`}
        >
          <div className="font-medium mb-1">Del likt</div>
          <div className="text-xs text-gray-500">Fordel verdiene på {pListOrders.length} ordrer</div>
        </button>
      </div>
    </div>
  );

  // Render warning for different addresses
  const renderDifferentAddressWarning = () => (
    <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <h4 className="text-lg font-semibold text-orange-900 mb-1">
            Ulike leveringsadresser
          </h4>
          <p className="text-sm text-orange-700">
            Ordrene har ulike leveringsadresser og må registreres individuelt.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-4xl sm:my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-lg sm:text-xl font-semibold">Registrer leveranseinformasjon</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 sm:p-6 max-h-[calc(100vh-140px)] sm:max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Scenario: Different addresses warning */}
          {scenario === 'multi-different-address' && renderDifferentAddressWarning()}

          {/* Scenario: Multi-order same address - mode selection */}
          {scenario === 'multi-same-address' && showModeSelection && renderModeSelection()}

          {/* Aggregation method selection for consolidation */}
          {deliveryMode === 'consolidate' && showAggregationSelection && renderAggregationSelection()}

          {/* PList Section - shown for single order or consolidation mode */}
          {(scenario === 'single' || scenario === 'no-orders' || (deliveryMode === 'consolidate' && aggregationMethod)) && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="text-lg font-semibold text-blue-900 mb-4">
                  {scenario === 'single' ? `Plukkliste #${pListId} (1 ordre)` :
                   deliveryMode === 'consolidate' ? `Samlasting - ${pListOrders.length} ordrer` :
                   `Plukkliste #${pListId}`}
                </h4>
                {scenario === 'single' && pListOrders[0] && (
                  <p className="text-sm text-blue-700 mb-4">
                    Ordre #{pListOrders[0].orderNo} - {pListOrders[0].customerName || 'Ukjent kunde'}
                    {pListOrders[0].delAddress1 && `, ${pListOrders[0].delAddress1}`}
                    {pListOrders[0].delCity && `, ${pListOrders[0].delCity}`}
                  </p>
                )}
                {deliveryMode === 'consolidate' && (
                  <p className="text-sm text-blue-700 mb-4">
                    Felles leveringsadresse: {pListOrders[0]?.delAddress1 || 'Ukjent'}, {pListOrders[0]?.delCity || ''}
                  </p>
                )}
                {renderDeliveryInfoFields(pListData, handlePListDataChange)}

                {/* ShipmentLine editor for single order or consolidation */}
                <div className="mt-4">
                  {scenario === 'single' && pListOrders[0] ? (
                    <ShipmentLineEditor
                      pListId={pListId}
                      orderNo={pListOrders[0].orderNo}
                      initialLines={pListOrdersData[0]?.shipmentLines.map(sl => ({
                        ...sl,
                        id: 0,
                        shipmentID: 0,
                        lineStatus: 0
                      } as ShipmentLineDto)) || []}
                      onChange={(lines) => handleShipmentLinesChange(pListOrders[0].orderNo, lines)}
                      expanded={pListOrdersData[0]?.shipmentLinesExpanded}
                      onExpandedChange={(expanded) => handleShipmentLinesExpandedChange(pListOrders[0].orderNo, expanded)}
                      disabled={isSubmitting}
                    />
                  ) : deliveryMode === 'consolidate' ? (
                    <ShipmentLineEditor
                      pListId={pListId}
                      orderNo={pListOrders[0]?.orderNo || 0}
                      initialLines={shipmentLinesForConsolidation.map(sl => ({
                        ...sl,
                        id: 0,
                        shipmentID: 0,
                        lineStatus: 0
                      } as ShipmentLineDto))}
                      onChange={setShipmentLinesForConsolidation}
                      disabled={isSubmitting}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* PListOrders Section - shown for per-order mode or forced per-order */}
          {(deliveryMode === 'per-order' || deliveryMode === 'forced-per-order') && pListOrders.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                Ordrer ({pListOrders.length})
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Klikk på en ordre for å registrere leveranseinformasjon.
              </p>

              <div className="space-y-3">
                {pListOrdersData.map((order) => (
                  <div
                    key={order.orderNo}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Order Header */}
                    <button
                      onClick={() => toggleOrderExpanded(order.orderNo)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="text-left">
                        <span className="font-medium text-gray-900">
                          Ordre #{order.orderNo}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          {order.customerName || 'Ukjent kunde'}
                        </span>
                        {order.delAddress1 && (
                          <div className="text-xs text-gray-400">
                            {order.delAddress1}, {order.delCity}
                          </div>
                        )}
                      </div>
                      {order.expanded ? (
                        <ChevronUp size={20} className="text-gray-600" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-600" />
                      )}
                    </button>

                    {/* Order Content */}
                    {order.expanded && (
                      <div className="p-4 bg-white space-y-4">
                        {renderDeliveryInfoFields(
                          order,
                          (field, value) => handlePListOrderDataChange(order.orderNo, field, value)
                        )}

                        {/* Kolli-detaljer (ShipmentLine) */}
                        <div className="mt-4">
                          <ShipmentLineEditor
                            pListId={pListId}
                            orderNo={order.orderNo}
                            initialLines={order.shipmentLines.map(sl => ({
                              ...sl,
                              id: 0,
                              shipmentID: 0,
                              lineStatus: 0
                            } as ShipmentLineDto))}
                            onChange={(lines) => handleShipmentLinesChange(order.orderNo, lines)}
                            expanded={order.shipmentLinesExpanded}
                            onExpandedChange={(expanded) => handleShipmentLinesExpandedChange(order.orderNo, expanded)}
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No orders message */}
          {pListOrders.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                Ingen ordre-relasjoner funnet for denne plukklisten. Kun plukkliste-informasjon vil bli lagret.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] order-2 sm:order-1"
            disabled={isSubmitting}
          >
            Avbryt
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px] order-1 sm:order-2"
            disabled={isSubmitting || (deliveryMode === 'consolidate' && !aggregationMethod)}
          >
            {isSubmitting ? 'Lagrer...' : 'Bekreft'}
          </button>
        </div>
      </div>
    </div>
  );
};
